import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  alpha,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Button,
  ButtonGroup,
  Stack,
  Divider,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ViewColumn as ViewColumnIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as BudgetIcon,
  Campaign as CampaignIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { spacing } from '../../theme/spacing';
import { ChartLoadingSkeleton, ChartEmptyState } from '../charts/ChartLoadingStates';

/**
 * Enhanced OOH Campaign Table - Compact redesigned table:
 * - Beautiful responsive design with animations
 * - Advanced filtering and search functionality
 * - Column visibility controls
 * - Progress indicators and trend visualization
 * - Export functionality
 * - Mobile-optimized layout
 * - Compact styling for better space utilization
 */

const EnhancedOOHCampaignTable = ({ 
  data, 
  loading,
  onRefresh,
  onExport,
  animate = true,
  className = '',
  sx = {},
  ...props 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({
    totalBudget: true,
    longtermPlanning: true,
    campaignBase: true, // Always show campaign base columns
    totalPlanning: true,
    savings: true
  });

  // Format currency
  const formatCurrency = useCallback((value) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('vi-VN', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(value);
  }, []);

  // Sample/default data
  const defaultData = [
    {
      initiative: 'Heineken',
      total_budget: 75000000000,
      longterm_planning_ytd: 8858254301,
      longterm_planning_ytg: 0,
      total_longterm_planning: 8858254301,
      given_campaign_base: {
        lcdp: 8858254301,
        led_short_term: 0,
        billboard_short_term: 0,
        street_led: 0,
        welcome_gate: 0,
        other: 0
      },
      lcdp_streetled_confirmed: 8858254301,
      total_planning_2025: 8858254301,
      total_saving: {
        longterm_unused: 66141745699,
        planning_actual_saving: 0,
        actual_saving: 0
      }
    },
    {
      initiative: 'Tiger',
      total_budget: 142810876400,
      longterm_planning_ytd: 50150348000,
      longterm_planning_ytg: 0,
      total_longterm_planning: 50150348000,
      given_campaign_base: {
        lcdp: 50150348000,
        led_short_term: 0,
        billboard_short_term: 0,
        street_led: 0,
        welcome_gate: 0,
        other: 0
      },
      lcdp_streetled_confirmed: 50150348000,
      total_planning_2025: 50150348000,
      total_saving: {
        longterm_unused: 92660528400,
        planning_actual_saving: 0,
        actual_saving: 0
      }
    }
  ];

  // Process and transform data to match OOH structure
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return defaultData;
    
    return data.map(row => {
      // Store format data first to prevent overriding
      const formatData = {
        lcdp: row.lcdp || 0,
        led_short_term: row.led_short_term || 0,
        billboard_short_term: row.billboard_short_term || 0,
        street_led: row.street_led || 0,
        welcome_gate: row.welcome_gate || 0,
        other: row.other || 0
      };
      
      // Map backend data to match OOHCampaignTable structure
      // Derive safe brand text (avoid passing object to React children)
      const brandText = 
        (typeof row.brand === 'string' && row.brand) ||
        row.brand_name ||
        (row.brand && (row.brand.brand_name || null)) ||
        'Unknown Brand';

      const mappedRow = {
        // Original data for reference FIRST
        ...row,
        
        // Core brand info
        initiative: brandText, // Always a string
        
        // Budget data mapping from API
        total_budget: row.given_cost || row.total_budget || 0, // TOTAL OOH GIVEN BUDGET
        longterm_planning_ytd: row.ooh_longterm_planning_ytd || row.longterm_planning_ytd || 0, // OOH LONG-TERM PLANNING YTD
        longterm_planning_ytg: row.ooh_longterm_planning_ytg || row.longterm_planning_ytg || 0, // OOH LONG-TERM PLANNING YTG
        total_longterm_planning: row.total_longterm_planning || (row.ooh_longterm_planning_ytd || 0) + (row.ooh_longterm_planning_ytg || 0), // TOTAL LONGTERM PLANNING
        
        // Given campaign base structure - use stored format data
        given_campaign_base: formatData,
        
        // Calculated fields
        lcdp_streetled_confirmed: (row.lcdp || 0) + (row.street_led || 0),
        total_planning_2025: row.total_planning_2025 || (row.lcdp || 0) + (row.led_short_term || 0) + (row.billboard_short_term || 0) + (row.street_led || 0) + (row.welcome_gate || 0) + (row.other || 0),
        
        // Savings structure
        total_saving: {
          longterm_unused: row.longterm_unused || Math.max(0, (row.given_cost || 0) - (row.total_longterm_planning || 0)),
          planning_actual_saving: row.planning_actual_saving || 0,
          actual_saving: row.actual_saving || 0
        }
      };
      
      return mappedRow;
    });
  }, [data]);

  // Get unique brands
  const brandList = useMemo(() => {
    return [...new Set(processedData.map(row => row.initiative))];
  }, [processedData]);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = processedData;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(row =>
        row.initiative.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Brand filter
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(row => selectedBrands.includes(row.initiative));
    }

    return filtered;
  }, [processedData, searchTerm, selectedBrands]);

  // Calculate totals
  const totals = useMemo(() => {
    return filteredData.reduce((acc, row) => {
      acc.total_budget += row.total_budget || 0;
      acc.longterm_planning_ytd += row.longterm_planning_ytd || 0;
      acc.longterm_planning_ytg += row.longterm_planning_ytg || 0;
      acc.total_longterm_planning += row.total_longterm_planning || 0;
      acc.lcdp_streetled_confirmed += row.lcdp_streetled_confirmed || 0;
      acc.total_planning_2025 += row.total_planning_2025 || 0;
      
      const gcb = row.given_campaign_base || {};
      acc.given_campaign_base.lcdp += gcb.lcdp || 0;
      acc.given_campaign_base.led_short_term += gcb.led_short_term || 0;
      acc.given_campaign_base.billboard_short_term += gcb.billboard_short_term || 0;
      acc.given_campaign_base.street_led += gcb.street_led || 0;
      acc.given_campaign_base.welcome_gate += gcb.welcome_gate || 0;
      acc.given_campaign_base.other += gcb.other || 0;
      
      const ts = row.total_saving || {};
      acc.total_saving.longterm_unused += ts.longterm_unused || 0;
      acc.total_saving.planning_actual_saving += ts.planning_actual_saving || 0;
      acc.total_saving.actual_saving += ts.actual_saving || 0;
      
      return acc;
    }, {
      total_budget: 0,
      longterm_planning_ytd: 0,
      longterm_planning_ytg: 0,
      total_longterm_planning: 0,
      given_campaign_base: {
        lcdp: 0,
        led_short_term: 0,
        billboard_short_term: 0,
        street_led: 0,
        welcome_gate: 0,
        other: 0
      },
      lcdp_streetled_confirmed: 0,
      total_planning_2025: 0,
      total_saving: {
        longterm_unused: 0,
        planning_actual_saving: 0,
        actual_saving: 0
      }
    });
  }, [filteredData]);

  // Brand color mapping
  const getBrandColor = (brand) => {
    const colors = {
      'Heineken': theme.palette.primary.main,
      'Tiger': theme.palette.warning.main,
      'Larue': theme.palette.info.main,
      'Bia Việt': theme.palette.success.main,
      'Strongbow': theme.palette.secondary.main,
      'Heineken 0.0': theme.palette.primary.light,
      'Edelweiss': theme.palette.grey[600]
    };
    return colors[brand] || theme.palette.grey[500];
  };

  // Render progress bar
  const renderProgressBar = (value, max, color = theme.palette.primary.main) => {
    const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    
    return (
      <Box sx={{ width: '100%', minWidth: 50 }}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.grey[300], 0.3),
            '& .MuiLinearProgress-bar': {
              borderRadius: 2,
              backgroundColor: color
            }
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
          {percentage.toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <ChartLoadingSkeleton height={400} />
      </Box>
    );
  }

  return (
    <Card 
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        overflow: 'hidden',
        maxWidth: '100%',
        ...sx
      }}
      className={className}
      {...props}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key="table-content"
          variants={containerVariants}
          initial={animate ? "hidden" : "visible"}
          animate="visible"
          exit="hidden"
        >
          {/* Header */}
          <motion.div variants={itemVariants}>
            <Box sx={{ p: 2, pb: 1 }}>
              {/* Title and actions */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                mb: 2
              }}>
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1.1rem'
                    }}
                  >
                    <CampaignIcon sx={{ color: theme.palette.primary.main, fontSize: '1.2rem' }} />
                    OOH 2025 Campaign Analytics
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    Comprehensive overview of Out-of-Home campaign budgets and performance
                  </Typography>
                </Box>

                {/* Action buttons */}
                <Stack direction="row" spacing={0.5}>
                  {onRefresh && (
                    <Tooltip title="Refresh Data">
                      <IconButton onClick={onRefresh} size="small">
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Tooltip title="Column Visibility">
                    <IconButton size="small">
                      <ViewColumnIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  {onExport && (
                    <Tooltip title="Export Data">
                      <IconButton onClick={onExport} size="small">
                        <ExportIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>

              {/* Summary Stats */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(4, 1fr)'
                },
                gap: 1.5,
                mb: 2,
                p: 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: '0.9rem' }}>
                    {filteredData.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Total Brands
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.success.main, fontSize: '0.9rem' }}>
                    {formatCurrency(totals.total_budget)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Total Budget
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.info.main, fontSize: '0.9rem' }}>
                    {formatCurrency(totals.total_longterm_planning)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Planned
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.warning.main, fontSize: '0.9rem' }}>
                    {formatCurrency(totals.total_saving.longterm_unused)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Unused Budget
                  </Typography>
                </Box>
              </Box>

              {/* Filters */}
              <Box sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                mb: 2,
                alignItems: 'center'
              }}>
                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search brands..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      fontSize: '0.8rem',
                      height: 35,
                      minHeight: 35
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 0.5
                    }
                  }}
                />

                {/* Brand filter */}
                <FormControl size="small" sx={{ 
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    height: 35,
                    minHeight: 35,
                    borderRadius: 1.5
                  }
                }}>
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Filter Brands</InputLabel>
                  <Select
                    multiple
                    value={selectedBrands}
                    onChange={(e) => setSelectedBrands(e.target.value)}
                    label="Filter Brands"
                    sx={{ 
                      fontSize: '0.8rem',
                      '& .MuiSelect-select': {
                        py: 0.5
                      }
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={value} 
                            size="small"
                            sx={{
                              backgroundColor: alpha(getBrandColor(value), 0.1),
                              color: getBrandColor(value),
                              border: `1px solid ${alpha(getBrandColor(value), 0.3)}`,
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {brandList.map((brand) => (
                      <MenuItem key={brand} value={brand} sx={{ fontSize: '0.8rem' }}>
                        {brand}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Removed view mode toggle */}
              </Box>
            </Box>
          </motion.div>

          <Divider />

          {/* Table */}
          <motion.div variants={itemVariants}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  {/* Main header */}
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        zIndex: 3,
                        fontSize: '0.8rem'
                      }} 
                      rowSpan={2}
                    >
                      OOH 2025
                    </TableCell>
                    
                    {visibleColumns.totalBudget && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 700,
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.8rem'
                        }} 
                        rowSpan={2}
                      >
                        Total Budget
                      </TableCell>
                    )}
                    
                    {visibleColumns.longtermPlanning && !isMobile && (
                      <>
                        <TableCell 
                          sx={{ 
                            fontWeight: 700,
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            textAlign: 'center',
                            fontSize: '0.8rem'
                          }} 
                          colSpan={3}
                        >
                          Long-term Planning
                        </TableCell>
                      </>
                    )}
                    
                    {visibleColumns.campaignBase && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 700,
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.8rem'
                        }} 
                        colSpan={6}
                      >
                        Campaign Base
                      </TableCell>
                    )}
                    
                    {visibleColumns.totalPlanning && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 700,
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.8rem'
                        }} 
                        rowSpan={2}
                      >
                        Total Planning 2025
                      </TableCell>
                    )}
                    
                    {visibleColumns.savings && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 700,
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.8rem'
                        }} 
                        colSpan={isMobile ? 1 : 2}
                      >
                        Savings
                      </TableCell>
                    )}
                  </TableRow>
                  
                  {/* Sub header */}
                  <TableRow>
                    {visibleColumns.longtermPlanning && !isMobile && (
                      <>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          YTD
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          YTG
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          Total
                        </TableCell>
                      </>
                    )}
                    
                    {visibleColumns.campaignBase && (
                      <>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          LCD/DP
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          LED Short-term
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          Billboard
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          Street LED
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          Welcome Gate
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          Other
                        </TableCell>
                      </>
                    )}
                    
                    {visibleColumns.savings && (
                      <>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.primary.main, 0.7),
                          color: theme.palette.primary.contrastText,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}>
                          Unused Budget
                        </TableCell>
                        {!isMobile && (
                          <TableCell sx={{ 
                            fontWeight: 600,
                            backgroundColor: alpha(theme.palette.primary.main, 0.7),
                            color: theme.palette.primary.contrastText,
                            textAlign: 'center',
                            fontSize: '0.75rem'
                          }}>
                            Utilization
                          </TableCell>
                        )}
                      </>
                    )}
                  </TableRow>
                </TableHead>
                
                <TableBody>
                  <AnimatePresence>
                    {filteredData.map((row, index) => (
                      <motion.tr
                        key={row.initiative}
                        initial={animate ? { opacity: 0, x: -20 } : undefined}
                        animate={animate ? { opacity: 1, x: 0 } : undefined}
                        transition={animate ? { delay: index * 0.1, duration: 0.3 } : undefined}
                        style={{
                          backgroundColor: index % 2 === 0 ? 'transparent' : alpha(theme.palette.grey[50], 0.5)
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: '0.7rem',
                                bgcolor: getBrandColor(row.initiative),
                                fontWeight: 600
                              }}
                            >
                              {row.initiative[0]}
                            </Avatar>
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {row.initiative}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        {visibleColumns.totalBudget && (
                          <TableCell align="right">
                            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                              {formatCurrency(row.total_budget)}
                            </Typography>
                          </TableCell>
                        )}
                        
                        {visibleColumns.longtermPlanning && !isMobile && (
                          <>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(row.longterm_planning_ytd)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(row.longterm_planning_ytg)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                {formatCurrency(row.total_longterm_planning)}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                        
                        {visibleColumns.campaignBase && (
                          <>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(row.given_campaign_base?.lcdp)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(row.given_campaign_base?.led_short_term)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(row.given_campaign_base?.billboard_short_term)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(row.given_campaign_base?.street_led)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(row.given_campaign_base?.welcome_gate)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(row.given_campaign_base?.other)}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                        
                        {visibleColumns.totalPlanning && (
                          <TableCell align="right">
                            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                              {formatCurrency(row.total_planning_2025)}
                            </Typography>
                          </TableCell>
                        )}
                        
                        {visibleColumns.savings && (
                          <>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                  {row.total_saving?.longterm_unused > 0 ? (
                                    <TrendingDownIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
                                  ) : (
                                    <TrendingUpIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
                                  )}
                                  <Typography 
                                    variant="caption"
                                    sx={{
                                      color: row.total_saving?.longterm_unused > 0 
                                        ? theme.palette.warning.main 
                                        : theme.palette.success.main,
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {formatCurrency(row.total_saving?.longterm_unused)}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            {!isMobile && (
                              <TableCell align="right">
                                {renderProgressBar(
                                  row.total_longterm_planning, 
                                  row.total_budget,
                                  getBrandColor(row.initiative)
                                )}
                              </TableCell>
                            )}
                          </>
                        )}
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                  
                  {/* Total row */}
                  <TableRow sx={{
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    '& .MuiTableCell-root': {
                      fontWeight: 700,
                      borderTop: `2px solid ${theme.palette.divider}`,
                      fontSize: '0.75rem'
                    }
                  }}>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                        TOTAL
                      </Typography>
                    </TableCell>
                    
                    {visibleColumns.totalBudget && (
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                          {formatCurrency(totals.total_budget)}
                        </Typography>
                      </TableCell>
                    )}
                    
                    {visibleColumns.longtermPlanning && !isMobile && (
                      <>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.longterm_planning_ytd)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.longterm_planning_ytg)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.total_longterm_planning)}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    
                    {visibleColumns.campaignBase && (
                      <>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.given_campaign_base.lcdp)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.given_campaign_base.led_short_term)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.given_campaign_base.billboard_short_term)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.given_campaign_base.street_led)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.given_campaign_base.welcome_gate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.given_campaign_base.other)}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    
                    {visibleColumns.totalPlanning && (
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                          {formatCurrency(totals.total_planning_2025)}
                        </Typography>
                      </TableCell>
                    )}
                    
                    {visibleColumns.savings && (
                      <>
                        <TableCell align="right">
                          <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.warning.main, fontSize: '0.75rem' }}>
                            {formatCurrency(totals.total_saving.longterm_unused)}
                          </Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell align="right">
                            {renderProgressBar(
                              totals.total_longterm_planning,
                              totals.total_budget,
                              theme.palette.info.main
                            )}
                          </TableCell>
                        )}
                      </>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </Card>
  );
};

export default EnhancedOOHCampaignTable;